import { Component } from '@angular/core';
import { SupplierModel } from 'src/app/core/models/supplier.model';
import { SupplierService } from './supplier.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SupplierCreateUpdateDeleteComponent } from './modals/supplier-create-update-delete/supplier-create-update-delete.component';
import { bubbleColor, ColumnIndicator, FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import {
  ELECTRONIC_INVOICE_ICON,
  ELECTRONIC_INVOICE_ICON_COLOR,
  ELECTRONIC_INVOICE_LEGEND_LABEL,
} from './supplier.constants';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { CategoryService } from '../../settings/category/category.service';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { ProductService } from '../product/product.service';
import { PrestationService } from '../prestation/prestation.service';
import { FormModalConfirmWithListComponent } from '../../../../core/base/components/form-modal-confirm-with-list/form-modal-confirm-with-list.component';
import { SupplierType } from '../../../../core/enums/supplier-type.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { PermissionMessageService } from '../../../../core/services/permission-message.service';

@Component({
  selector: 'vex-supplier',
  templateUrl: './supplier.component.html',
  styleUrls: ['./supplier.component.scss'],
})
export class SupplierComponent extends DataTableBase<SupplierModel> {
  ENUM_PERMISSION = ENUM_PERMISSIONS;

  feisColumns: Array<FeiColumn> = [
    {
      label: 'Dénomination',
      column: 'label',
      propertyDisplay: null,
      canUpdate: false,
      canDelete: false,
      statusClassFunc: this.isLinkedToCurrentContract,
      indicatorFunc: this.electronicInvoicePlatformIndicator,
    },
    { label: 'Type', column: 'type', propertyDisplay: null, canUpdate: false, canDelete: false },
    { label: 'N° siren', column: 'sirenNumber', propertyDisplay: null, canUpdate: false, canDelete: false },
    {
      label: '',
      column: 'actions',
      canDelete: false,
      canUpdate: false,
      canArchive: this.authService.hasPermission(ENUM_PERMISSIONS.DELETE_SUPPLIER),
    },
  ];

  legendData = [
    { description: 'Fournisseur présent dans un contrat actif', color: '#FFC0CB' },
    {
      description: ELECTRONIC_INVOICE_LEGEND_LABEL,
      icon: ELECTRONIC_INVOICE_ICON,
      iconColor: ELECTRONIC_INVOICE_ICON_COLOR,
    },
  ];

  entityName = 'FOURNISSEURS REFERENCES';
  public readonly supplierTypes = Object.values(SupplierType);
  selectedSupplierTypes: SupplierType[] = [];

  // Requête lancée à la 1re ouverture du popup (async pipe), puis rejouée depuis le cache (PRED-3271)
  readonly families$ = this.categoryService.getPurchaseFamilies().pipe(shareReplay(1));
  supplierTypesPermission = false;

  canShowArchivePermission = false;

  // PRED-3175 : pagination seule ; la persistance des filtres de cette page legacy relève de la carte de suivi
  protected persistFiltersInUrl = false;

  constructor(
    public service: SupplierService,
    private dialog: MatDialog,
    private router: Router,
    private categoryService: CategoryService,
    private productService: ProductService,
    private prestationService: PrestationService,
    public authService: AuthService,
    private snackbar: SnackbarService,
    private permissionMessageService: PermissionMessageService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  protected override setupTable(): void {
    this.popupForm = new UntypedFormGroup({
      category: new UntypedFormControl(''),
    });

    this.subscription.add(
      this.authService.getCurrentUserPermissions$().subscribe(permissions => {
        this.canShowArchivePermission = permissions.includes(ENUM_PERMISSIONS.READ_SUPPLIER);
      }),
    );
    this.supplierTypesPermission = this.authService.isPredik();
  }

  onSupplierTypeChange(value: SupplierType, checked: boolean) {
    this.selectedSupplierTypes = checked
      ? [...this.selectedSupplierTypes, value]
      : this.selectedSupplierTypes.filter(t => t !== value);

    if (this.selectedSupplierTypes.length > 0) {
      this.filters.supplierType = this.selectedSupplierTypes;
    } else {
      delete this.filters.supplierType;
    }
    this.pageIndex = 1;
    this.initData();
  }

  isLinkedToCurrentContract(element: SupplierModel): bubbleColor {
    return element.isLinkedToCurrentContract ? 'red' : '';
  }

  /** Étoile jaune devant la dénomination quand le fournisseur est conforme PA/PREDIK360 (PRED-3268). */
  electronicInvoicePlatformIndicator(element: SupplierModel): ColumnIndicator | null {
    return element.electronicInvoicePlatform
      ? {
          icon: ELECTRONIC_INVOICE_ICON,
          iconColor: ELECTRONIC_INVOICE_ICON_COLOR,
          tooltip: ELECTRONIC_INVOICE_LEGEND_LABEL,
        }
      : null;
  }

  // Passer par les hooks de la base (et non surcharger initData) pour que la pagination
  // soit reflétée dans l'URL et restaurée au retour d'une fiche fournisseur (PRED-3175).
  protected override buildFilters(): Record<string, unknown> {
    return {
      ...this.filters,
      isLinkedToCurrentContract: true,
      // La liste est projetée (defaultFields + requiredFields) : demander le flag PA (PRED-3268).
      requiredFields: ['isLinkedToCurrentContract', 'electronicInvoicePlatform'],
    };
  }

  override beforeValidatePopupFilter(): void {
    this.filters.category = this.popupForm.controls.category.value?._id;
  }

  createItem(): void {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.CREATE_SUPPLIER)) {
      return;
    }
    this.dialog
      .open(SupplierCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((supplier: SupplierModel) => {
        if (supplier) {
          this.initData();
          this.snackbar.success('Fournisseur créé');
        }
      });
  }

  updateItem(element: SupplierModel): void {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return;
    }
    this.dialog
      .open(SupplierCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((supplier: SupplierModel) => {
        if (supplier) {
          if (supplier._id) {
            this.router.navigateByUrl('/management/supplier/' + supplier._id).then();
          } else {
            this.initData();
          }
        }
      });
  }

  deleteItem(element: SupplierModel) {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.DELETE_SUPPLIER)) {
      return;
    }
    this.dialog
      .open(SupplierCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.label}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((supplier: SupplierModel) => {
        if (supplier) {
          this.initData();
        }
      });
  }

  archiveItem(element: SupplierModel) {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.DELETE_SUPPLIER)) {
      return;
    }
    forkJoin({
      product: this.productService.getsupplierRelation(element._id),
      prestation: this.prestationService.getsupplierRelation(element._id),
    }).subscribe({
      next: ({ product, prestation }) => {
        const productAndPresta = [
          ...product.data.map(x => `Article - ${x.label}`),
          ...prestation.data.map(x => `Prestation - ${x.label}`),
        ];

        if (productAndPresta.length > 0) {
          this.dialog.open(FormModalConfirmWithListComponent, {
            data: {
              title: 'Archivage impossible',
              message: `Vous ne pouvez pas archiver ce fournisseur car il est affecté à des articles/prestation`,
              list: productAndPresta,
            },
          });
        } else {
          this.dialog
            .open(SupplierCreateUpdateDeleteComponent, {
              data: {
                defaults: element,
                name: `${element.label}`,
                mode: CrudModeEnum.Archive,
              },
            })
            .afterClosed()
            .subscribe((supplier: SupplierModel) => {
              if (supplier) {
                this.initData();
              }
            });
        }
      },
      // Sans ce filet, un échec du pré-contrôle (ex. 403 avalé par l'intercepteur) laissait le clic sans effet.
      error: error =>
        this.snackbar.danger(
          error?.status === 403
            ? this.permissionMessageService.buildAccessDeniedMessage()
            : 'Impossible de vérifier les articles et prestations liés à ce fournisseur',
        ),
    });
  }

  importItem() {
    this.dialog
      .open(SupplierCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((supplier: SupplierModel) => {
        if (supplier) {
          this.initData();
          this.snackbar.success('Fournisseur créé');
        }
      });
  }
}
